import type { TFunction } from "i18next";
import type { TeamPlayer } from "../../types";
import { ChevronRight } from "../icons";
import PlayerChip from "./PlayerChip";
import type { LineupEditingState } from "./StartersArea";
import type { LineupChip, UseLineupDndResult } from "./useLineupDnd";

interface NotInSquadZoneProps {
  teamType: "home" | "away";
  chips: LineupChip[];
  dnd: UseLineupDndResult;
  readOnly: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  editingPlayer: LineupEditingState | null;
  onStartEdit: (teamType: "home" | "away", player: TeamPlayer) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditJerseyChange: (value: number) => void;
  onEditNameChange: (value: string) => void;
  t: TFunction;
}

/**
 * Collapsed-by-default zone for roster players who are not part of this
 * match at all (the old unchecked-checkbox state). Chips here are excluded
 * from the saved `LineupPlayerSelection[]` entirely — moving a chip back
 * out (tap or drag) restores it to the bench.
 */
export default function NotInSquadZone({
  teamType,
  chips,
  dnd,
  readOnly,
  expanded,
  onToggleExpanded,
  editingPlayer,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditJerseyChange,
  onEditNameChange,
  t,
}: NotInSquadZoneProps) {
  const zoneProps = dnd.zoneProps("excluded");
  const isDropHover = dnd.dragOverZone === "excluded";

  return (
    <div>
      <button
        type="button"
        onClick={onToggleExpanded}
        className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 mb-1"
        data-testid={`${teamType}-not-in-squad-toggle`}
        aria-expanded={expanded}
      >
        <ChevronRight
          className={`h-4 w-4 transition-transform ${
            expanded ? "rotate-90" : ""
          }`}
        />
        {t("lineupBoard.notInSquad", {
          count: chips.length,
          defaultValue: "Not in matchday squad ({{count}})",
        })}
      </button>
      {expanded && (
        <div
          {...zoneProps}
          data-testid={`${teamType}-not-in-squad-candidates`}
          className={`space-y-2 min-h-[3rem] rounded-lg p-1 mt-2 transition-colors md:max-h-[26rem] md:overflow-y-auto md:pr-1 lg:max-h-[32rem] ${
            isDropHover ? "bg-emerald-50 ring-2 ring-emerald-300" : ""
          }`}
        >
          {chips.length === 0 && (
            <p className="text-xs text-gray-400 italic p-2">
              {t("lineupBoard.notInSquadEmpty", {
                defaultValue:
                  "Drag or tap a bench player here to exclude them from this match.",
              })}
            </p>
          )}
          {chips.map(({ player, selectable }) => {
            const isEditing =
              editingPlayer?.teamType === teamType &&
              editingPlayer?.column === "excluded" &&
              editingPlayer?.playerId === player.player_id;
            return (
              <PlayerChip
                key={player.player_id}
                player={player}
                zone="excluded"
                readOnly={readOnly}
                toggleDisabled={!selectable}
                isDragging={dnd.draggingPlayerId === player.player_id}
                dragProps={dnd.chipProps(player, "excluded")}
                isEditing={Boolean(isEditing)}
                editValues={
                  isEditing && editingPlayer
                    ? {
                        jersey_number: editingPlayer.jersey_number,
                        player_name: editingPlayer.player_name,
                      }
                    : null
                }
                onStartEdit={() => onStartEdit(teamType, player)}
                onCancelEdit={onCancelEdit}
                onSaveEdit={onSaveEdit}
                onEditJerseyChange={onEditJerseyChange}
                onEditNameChange={onEditNameChange}
                t={t}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
